import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Plus, Trash2, Send, FileText, Upload, Users, CheckCircle, CheckCircle2, Clock, Eye, PenLine, ChevronRight, ArrowLeft, Download, MessageSquare } from 'lucide-react';
import { format } from 'date-fns';
import { fmtDate } from '@/lib/dateFmt';
import { AuditLogButton } from '@/components/AuditLogButton';
import { it } from 'date-fns/locale';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function DocumentiPage() {
  const [documents, setDocuments] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [cantieri, setCantieri] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [selectedWorkers, setSelectedWorkers] = useState([]);
  const [sendMessage, setSendMessage] = useState('');
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [sentDetails, setSentDetails] = useState([]);
  const [detailDoc, setDetailDoc] = useState(null);
  const [formData, setFormData] = useState({ title: '', description: '', cantiere_id: '', requires_signature: false });
  const [file, setFile] = useState(null);
  const [files, setFiles] = useState([]);  // iter64: supporto multi-file upload
  const [uploadTargets, setUploadTargets] = useState([]);
  const [uploadMessage, setUploadMessage] = useState('');
  const [showAllWorkersUpload, setShowAllWorkersUpload] = useState(false);
  // Chat view
  const [byWorker, setByWorker] = useState([]);
  const [selectedWorkerChat, setSelectedWorkerChat] = useState(null);
  const [activeTab, setActiveTab] = useState('documenti');

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [docsRes, workersRes, cantieriRes, byWorkerRes] = await Promise.all([
        axios.get(`${API}/documents`, { withCredentials: true }),
        axios.get(`${API}/users?role=operaio`, { withCredentials: true }),
        axios.get(`${API}/cantieri`, { withCredentials: true }),
        axios.get(`${API}/documents/by-worker`, { withCredentials: true })
      ]);
      setDocuments(docsRes.data);
      setWorkers(workersRes.data);
      setCantieri(cantieriRes.data);
      setByWorker(byWorkerRes.data);
    } catch (error) { console.error(error); }
    finally { setLoading(false); }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    // iter64: usa `files` (array) se presente, altrimenti fallback su singolo `file`
    const allFiles = files.length > 0 ? files : (file ? [file] : []);
    if (allFiles.length === 0) { toast.error('Seleziona almeno un file'); return; }
    if (!formData.title?.trim()) { toast.error('Inserisci un titolo'); return; }
    setUploading(true);
    try {
      const createdDocIds = [];
      let successCount = 0;
      let failCount = 0;

      // Loop: per ogni file, upload → create document record
      for (let i = 0; i < allFiles.length; i++) {
        const f = allFiles[i];
        try {
          const fd = new FormData();
          fd.append('file', f);
          const uploadRes = await axios.post(`${API}/upload`, fd, {
            withCredentials: true,
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          const filePath = uploadRes.data?.path;
          if (!filePath) throw new Error('Upload failed');

          // Se ci sono più file, appendi il nome al titolo per distinguerli
          const docTitle = allFiles.length > 1
            ? `${formData.title} - ${f.name}`
            : formData.title;

          const createRes = await axios.post(`${API}/documents`, {
            title: docTitle,
            description: formData.description,
            cantiere_id: formData.cantiere_id || null,
            file_url: `/api/files/${filePath}`,
            file_type: f.type || (f.name.split('.').pop() || ''),
            requires_signature: !!formData.requires_signature,
            category: 'generale'
          }, { withCredentials: true });

          if (createRes?.data?.id) {
            createdDocIds.push(createRes.data.id);
            successCount++;
          }
        } catch (err) {
          console.error(`Errore file ${f.name}:`, err);
          failCount++;
        }
      }

      if (failCount > 0) {
        toast.warning(`${failCount} file su ${allFiles.length} non caricati`);
      }

      // Step 3: se operai selezionati, invia tutti i doc creati
      if (uploadTargets.length > 0 && createdDocIds.length > 0) {
        let sentOk = 0;
        for (const docId of createdDocIds) {
          try {
            await axios.post(`${API}/documents/send`, {
              document_id: docId,
              worker_ids: uploadTargets,
              message: uploadMessage
            }, { withCredentials: true });
            sentOk++;
          } catch (sendErr) {
            console.error('Send error:', sendErr);
          }
        }
        if (sentOk === createdDocIds.length) {
          toast.success(`${successCount} documento/i caricato/i e inviato/i a ${uploadTargets.length} operaio/i`);
        } else {
          toast.warning(`${sentOk}/${createdDocIds.length} documenti inviati. Alcuni invii sono falliti.`);
        }
      } else if (successCount > 0) {
        toast.success(`${successCount} documento/i caricato/i`);
      }

      setUploadDialogOpen(false);
      setFormData({ title: '', description: '', cantiere_id: '', requires_signature: false });
      setFile(null);
      setFiles([]);
      setUploadTargets([]);
      setUploadMessage('');
      setShowAllWorkersUpload(false);
      fetchData();
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || error.message || 'Errore nel caricamento');
    }
    finally { setUploading(false); }
  };

  const openSendDialog = (doc) => { setSelectedDocument(doc); setSelectedWorkers([]); setSendMessage(''); setSendDialogOpen(true); };

  const handleSend = async () => {
    if (selectedWorkers.length === 0) { toast.error('Seleziona almeno un operaio'); return; }
    setSending(true);
    try {
      await axios.post(`${API}/documents/send`, { document_id: selectedDocument.id, worker_ids: selectedWorkers, message: sendMessage }, { withCredentials: true });
      toast.success('Documento inviato');
      setSendDialogOpen(false);
      fetchData();
    } catch (error) { toast.error('Errore'); }
    finally { setSending(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare il documento? Verrà rimosso anche dagli operai.')) return;
    try {
      await axios.delete(`${API}/documents/${id}`, { withCredentials: true });
      toast.success('Documento eliminato');
      fetchData();
    } catch { toast.error('Errore'); }
  };

  const openDetailDialog = async (doc) => {
    setDetailDoc(doc);
    try {
      const res = await axios.get(`${API}/documents/${doc.id}/sent-history`, { withCredentials: true });
      setSentDetails(res.data);
    } catch { setSentDetails([]); }
    setDetailDialogOpen(true);
  };

  const getFileUrl = (url) => {
    if (!url) return '#';
    if (url.startsWith('http')) return url;
    return `${API.replace('/api', '')}${url}`;
  };

  const getUploadButtonLabel = () => {
    if (uploading) return 'Caricamento...';
    if (uploadTargets.length > 0) return `Carica e invia a ${uploadTargets.length}`;
    return 'Carica';
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div></div>;

  return (
    <div className="space-y-6" data-testid="documenti-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">Documenti</h1>
          <p className="text-slate-500 mt-1">Gestisci e invia documenti agli operai</p>
        </div>
        <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-slate-900 text-white hover:bg-slate-800" data-testid="upload-doc-btn"><Plus className="h-4 w-4 mr-2" />Carica Documento</Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[480px] max-h-[90vh] flex flex-col p-0">
            <DialogHeader className="px-6 pt-6 pb-2"><DialogTitle>Carica Documento</DialogTitle></DialogHeader>
            <form onSubmit={handleUpload} className="flex flex-col flex-1 min-h-0">
              <div className="space-y-4 px-6 pb-4 overflow-y-auto flex-1">
              <div className="space-y-2"><Label>Titolo *</Label><Input value={formData.title} onChange={e => setFormData({...formData, title: e.target.value})} required data-testid="doc-title-input" /></div>
              <div className="space-y-2"><Label>Descrizione</Label><Textarea value={formData.description} onChange={e => setFormData({...formData, description: e.target.value})} /></div>
              <div className="space-y-2">
                <Label>Cantiere <span className="text-xs text-slate-400 font-normal">(opzionale — filtra gli operai)</span></Label>
                <Select value={formData.cantiere_id || 'none'} onValueChange={(v) => { setFormData({...formData, cantiere_id: v === 'none' ? '' : v}); setUploadTargets([]); setShowAllWorkersUpload(false); }}>
                  <SelectTrigger data-testid="upload-cantiere-select"><SelectValue placeholder="Nessun cantiere" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nessun cantiere</SelectItem>
                    {cantieri.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>File (puoi selezionarne più di uno)</Label>
                <div className="border-2 border-dashed border-slate-200 rounded-lg p-4 text-center hover:border-slate-400 transition-colors">
                  <input
                    type="file"
                    multiple
                    onChange={e => {
                      const selected = Array.from(e.target.files || []);
                      setFiles(prev => {
                        // Dedupe per name+size (evita di aggiungere lo stesso file due volte)
                        const seen = new Set(prev.map(x => `${x.name}::${x.size}`));
                        const merged = [...prev];
                        for (const f of selected) {
                          const key = `${f.name}::${f.size}`;
                          if (!seen.has(key)) {
                            merged.push(f);
                            seen.add(key);
                          }
                        }
                        return merged;
                      });
                      setFile(selected[0] || null);
                      e.target.value = '';  // reset input per ripermettere stesso file
                    }}
                    className="hidden"
                    id="file-upload"
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                    data-testid="document-file-input"
                  />
                  <label htmlFor="file-upload" className="cursor-pointer inline-block">
                    <Upload className="h-8 w-8 mx-auto text-slate-400 mb-2" />
                    <p className="text-sm text-slate-600">
                      {files.length > 0
                        ? `+ Aggiungi altri file (${files.length} già selezionati)`
                        : 'Clicca per caricare uno o più file'}
                    </p>
                  </label>
                </div>
                {/* Lista file selezionati */}
                {files.length > 0 && (
                  <div className="border rounded-md divide-y bg-slate-50 max-h-40 overflow-y-auto" data-testid="files-list">
                    {files.map((f, idx) => (
                      <div key={`${f.name}-${idx}`} className="flex items-center justify-between px-3 py-1.5 text-xs">
                        <span className="truncate flex-1 text-slate-700" title={f.name}>
                          📎 {f.name} <span className="text-slate-400">({(f.size / 1024).toFixed(0)} KB)</span>
                        </span>
                        <button
                          type="button"
                          onClick={() => setFiles(prev => prev.filter((_, i) => i !== idx))}
                          className="text-red-500 hover:text-red-700 ml-2"
                          data-testid={`remove-file-${idx}`}
                          title="Rimuovi"
                        >✕</button>
                      </div>
                    ))}
                  </div>
                )}
                {files.length > 1 && (
                  <p className="text-[10px] text-slate-500">
                    Verranno creati {files.length} documenti separati. Il titolo &quot;{formData.title || '...'}&quot; verrà appeso al nome di ciascun file.
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Checkbox id="requires_signature" checked={formData.requires_signature} onCheckedChange={checked => setFormData({...formData, requires_signature: checked})} />
                <Label htmlFor="requires_signature" className="text-sm font-normal">Richiede firma digitale</Label>
              </div>
              <div className="space-y-2 border-t pt-3">
                <div className="flex items-center justify-between">
                  <Label className="flex items-center gap-1.5"><Users className="h-4 w-4" />Invia agli Operai <span className="text-xs text-slate-400 font-normal">(opzionale)</span></Label>
                  {uploadTargets.length > 0 && <span className="text-xs text-blue-600 font-medium">{uploadTargets.length} selezionati</span>}
                </div>
                {(() => {
                  const selectedCantiere = cantieri.find(c => c.id === formData.cantiere_id);
                  const activeWorkers = workers.filter(w => w.status !== 'sospeso');
                  const assignedIds = selectedCantiere ? (selectedCantiere.assigned_workers || []) : null;
                  const displayWorkers = (assignedIds && !showAllWorkersUpload)
                    ? activeWorkers.filter(w => assignedIds.includes(w.id))
                    : activeWorkers;
                  return (
                    <>
                      {selectedCantiere && (
                        <div className="flex items-center justify-between text-xs bg-blue-50 border border-blue-100 rounded-md px-2.5 py-1.5">
                          <span className="text-blue-700">
                            {showAllWorkersUpload
                              ? `Tutti gli operai (${activeWorkers.length})`
                              : `Operai di "${selectedCantiere.name}" (${displayWorkers.length})`}
                          </span>
                          <button
                            type="button"
                            onClick={() => { setShowAllWorkersUpload(v => !v); setUploadTargets([]); }}
                            className="text-blue-600 hover:text-blue-800 font-medium underline"
                            data-testid="toggle-show-all-workers"
                          >
                            {showAllWorkersUpload ? 'Mostra solo del cantiere' : 'Mostra tutti'}
                          </button>
                        </div>
                      )}
                      <div className="space-y-1.5 max-h-40 overflow-y-auto border border-slate-200 rounded-md p-2.5" data-testid="upload-workers-list">
                        <div className="flex items-center gap-2 pb-1.5 border-b border-slate-100">
                          <Checkbox
                            id="upload-select-all"
                            checked={displayWorkers.length > 0 && displayWorkers.every(w => uploadTargets.includes(w.id))}
                            onCheckedChange={(checked) => setUploadTargets(checked ? displayWorkers.map(w => w.id) : [])}
                            data-testid="upload-select-all-workers"
                          />
                          <Label htmlFor="upload-select-all" className="text-sm font-medium cursor-pointer">Seleziona tutti</Label>
                        </div>
                        {displayWorkers.length === 0 ? (
                          <p className="text-xs text-slate-400 py-2 text-center">
                            {selectedCantiere && !showAllWorkersUpload ? 'Nessun operaio assegnato a questo cantiere' : 'Nessun operaio attivo'}
                          </p>
                        ) : displayWorkers.map(worker => (
                          <div key={worker.id} className="flex items-center gap-2">
                            <Checkbox
                              id={`upload-w-${worker.id}`}
                              checked={uploadTargets.includes(worker.id)}
                              onCheckedChange={(checked) => setUploadTargets(prev => checked ? [...prev, worker.id] : prev.filter(id => id !== worker.id))}
                              data-testid={`upload-worker-${worker.id}`}
                            />
                            <Label htmlFor={`upload-w-${worker.id}`} className="text-sm font-normal cursor-pointer flex-1">{worker.name}</Label>
                          </div>
                        ))}
                      </div>
                    </>
                  );
                })()}
                {uploadTargets.length > 0 && (
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-600">Messaggio per gli operai (opzionale)</Label>
                    <Textarea
                      value={uploadMessage}
                      onChange={e => setUploadMessage(e.target.value)}
                      placeholder="Es. Controllate la lista aggiornata..."
                      className="text-sm bg-white text-slate-900 placeholder:text-slate-400"
                      rows={2}
                      data-testid="upload-message-input"
                    />
                  </div>
                )}
              </div>
              </div>
              <div className="flex justify-end gap-2 px-6 py-3 border-t bg-white rounded-b-lg"><Button type="button" variant="outline" onClick={() => setUploadDialogOpen(false)}>Annulla</Button><Button type="submit" className="bg-slate-900 text-white" disabled={uploading} data-testid="submit-upload-doc">{getUploadButtonLabel()}</Button></div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="documenti"><FileText className="h-4 w-4 mr-1.5" />Documenti</TabsTrigger>
          <TabsTrigger value="per-operaio"><MessageSquare className="h-4 w-4 mr-1.5" />Per Operaio {byWorker.length > 0 && `(${byWorker.length})`}</TabsTrigger>
        </TabsList>

        {/* Tab Documenti */}
        <TabsContent value="documenti">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {documents.length === 0 ? (
              <Card className="col-span-full border-slate-200"><CardContent className="py-12 text-center"><FileText className="h-12 w-12 mx-auto text-slate-300 mb-4" /><p className="text-slate-500">Nessun documento caricato</p></CardContent></Card>
            ) : documents.map(doc => {
              const sentTo = doc.sent_to || [];
              return (
                <Card key={doc.id} className="border-slate-200 hover:shadow-sm transition-all">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-slate-100 rounded-lg"><FileText className="h-5 w-5 text-slate-600" /></div>
                        <div><h3 className="font-medium text-slate-900">{doc.title}</h3><p className="text-xs text-slate-500">{fmtDate(doc.created_at, 'dd-MM-yyyy')}</p></div>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        {doc.requires_signature && <Badge variant="outline" className="text-amber-600 border-amber-200 bg-amber-50 text-xs">Firma</Badge>}
                        {sentTo.length > 0 && <Badge variant="outline" className="text-emerald-600 border-emerald-200 bg-emerald-50 text-xs"><CheckCircle className="h-3 w-3 mr-1" />Inviato a {sentTo.length}</Badge>}
                      </div>
                    </div>
                    {doc.description && <p className="text-sm text-slate-500 mb-2 line-clamp-2">{doc.description}</p>}
                    {sentTo.length > 0 && (
                      <div className="mb-3 p-2 bg-slate-50 rounded-md border border-slate-100 cursor-pointer hover:bg-slate-100" onClick={() => openDetailDialog(doc)}>
                        <p className="text-xs text-slate-400 mb-1.5 flex items-center gap-1"><Users className="h-3 w-3" />Inviato a {sentTo.length} — <span className="text-blue-500 underline">dettagli</span></p>
                        <div className="flex flex-wrap gap-1">{sentTo.map((s) => <span key={s.worker_id || s.name} className="text-xs px-2 py-0.5 bg-white border border-slate-200 rounded-full">{s.name}</span>)}</div>
                      </div>
                    )}
                    <div className="flex justify-between items-center pt-3 border-t border-slate-100 flex-wrap gap-1">
                      <Button variant="ghost" size="sm" onClick={() => openSendDialog(doc)} className="text-blue-600"><Send className="h-4 w-4 mr-1" />Invia</Button>
                      <div className="flex items-center gap-1">
                        <AuditLogButton fileId={doc.id} fileNome={doc.title} />
                        <Button variant="ghost" size="icon" onClick={() => handleDelete(doc.id)} className="text-red-600"><Trash2 className="h-4 w-4" /></Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>

        {/* Tab Per Operaio - Chat Style */}
        <TabsContent value="per-operaio">
          {!selectedWorkerChat ? (
            <div className="space-y-2">
              {byWorker.length === 0 ? (
                <Card className="border-slate-200"><CardContent className="py-12 text-center"><MessageSquare className="h-12 w-12 mx-auto text-slate-300 mb-4" /><p className="text-slate-500">Nessun documento inviato ancora</p></CardContent></Card>
              ) : byWorker.map(w => (
                <Card key={w.worker_id} className="border-slate-200 hover:border-slate-400 hover:shadow-sm transition-all cursor-pointer" onClick={() => setSelectedWorkerChat(w)} data-testid={`worker-chat-${w.worker_id}`}>
                  <CardContent className="p-4 flex items-center gap-4">
                    <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                      <span className="text-lg font-bold text-blue-700">{w.worker_name?.charAt(0)}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-slate-900">{w.worker_name}</h3>
                      <p className="text-xs text-slate-500">{w.total} document{w.total !== 1 ? 'i' : 'o'} inviat{w.total !== 1 ? 'i' : 'o'}</p>
                    </div>
                    {w.unread > 0 && <Badge className="bg-amber-100 text-amber-700 border-amber-200" variant="outline"><Clock className="h-3 w-3 mr-1" />{w.unread} non letti</Badge>}
                    <Badge variant="outline" className="text-emerald-600 border-emerald-200">{w.total - w.unread} letti</Badge>
                    <ChevronRight className="h-5 w-5 text-slate-400" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="space-y-4">
              <Button variant="ghost" className="text-slate-500 -ml-2" onClick={() => setSelectedWorkerChat(null)} data-testid="back-to-workers">
                <ArrowLeft className="h-4 w-4 mr-2" />Tutti gli operai
              </Button>
              <div className="flex items-center gap-4 mb-4">
                <div className="h-14 w-14 rounded-full bg-blue-100 flex items-center justify-center">
                  <span className="text-xl font-bold text-blue-700">{selectedWorkerChat.worker_name?.charAt(0)}</span>
                </div>
                <div>
                  <h2 className="text-xl font-bold text-slate-900">{selectedWorkerChat.worker_name}</h2>
                  <p className="text-sm text-slate-500">{selectedWorkerChat.total} documenti inviati</p>
                </div>
              </div>
              <div className="space-y-3">
                {selectedWorkerChat.documents.map(doc => (
                  <Card key={doc.id} className={`border-slate-200 ${doc.read ? '' : 'border-l-4 border-l-amber-400'}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <div className="p-2 bg-slate-100 rounded-lg shrink-0"><FileText className="h-5 w-5 text-slate-600" /></div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h4 className="font-medium text-slate-900">{doc.document_title}</h4>
                            {doc.read ? (
                              <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-xs" variant="outline"><Eye className="h-3 w-3 mr-1" />Letto</Badge>
                            ) : (
                              <Badge className="bg-amber-50 text-amber-700 border-amber-200 text-xs" variant="outline"><Clock className="h-3 w-3 mr-1" />Non letto</Badge>
                            )}
                            {doc.signed && <Badge className="bg-blue-50 text-blue-700 border-blue-200 text-xs" variant="outline"><PenLine className="h-3 w-3 mr-1" />Firmato</Badge>}
                          </div>
                          {doc.document_description && <p className="text-sm text-slate-500 mt-1">{doc.document_description}</p>}
                          {doc.message && <p className="text-sm text-blue-600 mt-1 italic">"{doc.message}"</p>}
                          <p className="text-xs text-slate-400 mt-2">{fmtDate(doc.created_at, 'dd MMM yyyy, HH:mm')}</p>
                        </div>
                        {doc.file_url && (
                          <a href={getFileUrl(doc.file_url)} target="_blank" rel="noopener noreferrer" className="shrink-0">
                            <Button variant="ghost" size="icon" className="text-blue-600"><Download className="h-4 w-4" /></Button>
                          </a>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Send Dialog */}
      <Dialog open={sendDialogOpen} onOpenChange={setSendDialogOpen}>
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader><DialogTitle>Invia "{selectedDocument?.title}"</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Seleziona Operai</Label>
              <div className="space-y-2 max-h-48 overflow-y-auto border border-slate-200 rounded-md p-3">
                <div className="flex items-center gap-2 mb-2 pb-2 border-b">
                  <Checkbox checked={selectedWorkers.length === workers.length} onCheckedChange={(checked) => setSelectedWorkers(checked ? workers.map(w => w.id) : [])} />
                  <span className="text-sm font-medium">Seleziona tutti</span>
                </div>
                {workers.map(worker => (
                  <div key={worker.id} className="flex items-center gap-2">
                    <Checkbox checked={selectedWorkers.includes(worker.id)} onCheckedChange={(checked) => setSelectedWorkers(prev => checked ? [...prev, worker.id] : prev.filter(id => id !== worker.id))} />
                    <span className="text-sm">{worker.name}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="space-y-2"><Label>Messaggio (opzionale)</Label><Textarea value={sendMessage} onChange={e => setSendMessage(e.target.value)} placeholder="Aggiungi un messaggio..." /></div>
            <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setSendDialogOpen(false)}>Annulla</Button><Button className="bg-blue-600 text-white hover:bg-blue-700" onClick={handleSend} disabled={sending}><Send className="h-4 w-4 mr-2" />{sending ? 'Invio...' : `Invia a ${selectedWorkers.length}`}</Button></div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader><DialogTitle>Dettaglio Invio - {detailDoc?.title}</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-4">
            {sentDetails.length === 0 ? <p className="text-slate-500 text-center py-4">Nessun dettaglio</p> : sentDetails.map((d) => (
              <div key={d.worker_id || `${d.worker_name}-${d.sent_at}`} className="p-3 bg-slate-50 rounded-lg border space-y-2" data-testid={`sent-detail-${d.worker_id}`}>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-3">
                    <div className="h-8 w-8 rounded-full bg-slate-200 flex items-center justify-center text-sm font-medium">{d.worker_name?.charAt(0)}</div>
                    <div>
                      <p className="font-medium text-sm">{d.worker_name}</p>
                      <p className="text-xs text-slate-400">Inviato {d.sent_at && fmtDate(d.sent_at, 'dd-MM-yyyy HH:mm')}</p>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {d.read_confirmed ? (
                      <Badge className="bg-emerald-100 text-emerald-700 text-xs border-emerald-300" variant="outline" data-testid={`confirmed-badge-${d.worker_id}`}>
                        <CheckCircle2 className="h-3 w-3 mr-1" />Conferma esplicita
                      </Badge>
                    ) : d.read ? (
                      <Badge className="bg-blue-50 text-blue-700 text-xs" variant="outline">
                        <Eye className="h-3 w-3 mr-1" />Aperto (non confermato)
                      </Badge>
                    ) : (
                      <Badge className="bg-amber-50 text-amber-700 text-xs" variant="outline">
                        <Clock className="h-3 w-3 mr-1" />Non letto
                      </Badge>
                    )}
                    {d.signed && <Badge className="bg-purple-50 text-purple-700 text-xs" variant="outline"><PenLine className="h-3 w-3 mr-1" />Firmato</Badge>}
                  </div>
                </div>
                {d.read_confirmed && d.read_confirmed_at && (
                  <p className="text-[11px] text-emerald-700 pl-11">
                    ✓ Lettura confermata il <b>{fmtDate(d.read_confirmed_at, 'dd-MM-yyyy HH:mm')}</b>
                    {d.read_confirmed_by_name ? ` da ${d.read_confirmed_by_name}` : ''}
                  </p>
                )}
                {d.signed && d.signed_at && (
                  <p className="text-[11px] text-purple-700 pl-11">
                    ✓ Firmato il <b>{fmtDate(d.signed_at, 'dd-MM-yyyy HH:mm')}</b>
                  </p>
                )}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
